// Checks whether the player exposes an optional accessor before calling it.
// oxlint-disable anti-slop/no-runtime-typeof
import { useCallback } from 'react';
import { VideoPlayerState } from '../types';
import { VideoItemReference } from 'types/sharedComponents';
import { trackVideoInteraction } from 'components/analytics/eventHandlerTracking';
import { getPlayerProvider } from '../utils';
import type { ReactPlayerInstance } from '../types';
import { isProdEnvironment } from 'utils/environment';

// ReactPlayer instance typing comes from shared VideoPlayer types

/**
 * Custom hook for tracking video interactions
 */
export const useVideoInteractionTracking = () => {
  const handleVideoInteraction = useCallback(
    (playerRef: ReactPlayerInstance | null, state: VideoPlayerState, video: VideoItemReference) => {
      if (!playerRef) return;

      try {
        // Helper functions to get timing data
        const getCurrentTime = () => {
          // Checks whether an optional callback was supplied before calling it.
          if (typeof playerRef.getCurrentTime === 'function') {
            return playerRef.getCurrentTime() ?? 0;
          }
          // Fallback for HTML5 video element
          const maybeHtml5 = // Checks whether an optional callback was supplied before calling it.
            // Checks whether an optional callback was supplied before calling it.
            (
              typeof playerRef.getInternalPlayer === 'function' ? playerRef.getInternalPlayer() : null
            ) as HTMLVideoElement | null;
          return maybeHtml5?.currentTime ?? 0;
        };

        const getDuration = () => {
          // Checks whether an optional callback was supplied before calling it.
          if (typeof playerRef.getDuration === 'function') {
            return playerRef.getDuration() ?? 0;
          }
          // Fallback for HTML5 video element
          const maybeHtml5 = // Checks whether an optional callback was supplied before calling it.
            // Checks whether an optional callback was supplied before calling it.
            (
              typeof playerRef.getInternalPlayer === 'function' ? playerRef.getInternalPlayer() : null
            ) as HTMLVideoElement | null;
          return maybeHtml5?.duration ?? 0;
        };

        let currentTime = getCurrentTime();
        const duration = getDuration();

        // Normalize for ended state
        if (state === VideoPlayerState.Ended) {
          currentTime = duration;
        }

        // Calculate percentage
        const percentComplete =
          state === VideoPlayerState.Ended
            ? '100.00'
            : duration > 0
              ? ((currentTime / duration) * 100).toFixed(2)
              : '0.00';

        // Get video metadata
        const title = video.video?.title || video.overlayLabel || 'Unknown Video Title';
        const videoUrl = video.video?.url || video.videoUrl || '';
        const provider = getPlayerProvider(videoUrl);

        trackVideoInteraction({
          title,
          current_time: currentTime.toFixed(2),
          duration: duration.toFixed(2),
          percent_complete: percentComplete,
          provider,
          status: state,
          url: videoUrl,
        });
      } catch (error) {
        if (!isProdEnvironment()) {
          console.warn('Video interaction tracking failed:', error);
        }
      }
    },
    [],
  );

  return { handleVideoInteraction };
};
